import React, {useState} from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Grid,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Radio,
  RadioGroup,
  Snackbar,
  Alert,
} from '@mui/material'
import {LocalizationProvider, DatePicker} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'

interface Person {
  reasonForVisit: string
  painLocation: string
  dateOfOnsetInjury: string
  ratePain: number
  typeOfPain: string[]
  howOftenPain: string
  relievePain: string
  previousTreatments: string
  painRelatedToInjury: string
}

const painScaleOptions = Array.from({length: 11}, (_, i) => ({label: i.toString(), value: i}))
const painTypeOptions = [
  {label: 'Sharp', value: 'sharp'},
  {label: 'Piercing', value: 'piercing'},
  {label: 'Aching', value: 'aching'},
  {label: 'Dull', value: 'dull'},
  {label: 'Shooting', value: 'shooting'},
  {label: 'Stabbing', value: 'stabbing'},
  {label: 'Tingling', value: 'tingling'},
  {label: 'Burning', value: 'burning'},
  {label: 'Other', value: 'other'},
]

interface PhysicalTherapistDialogProps {
  assessmentRecords: Person[]
  setAssessmentRecords: (records: Person[]) => void
  open: boolean
  onOpen: () => void
  onClose: () => void
  disabled?: boolean
}

const PhysicalTherapistDialog: React.FC<PhysicalTherapistDialogProps> = ({
  assessmentRecords,
  setAssessmentRecords,
  open,
  onOpen,
  onClose,
  disabled = false,
}) => {
  const [newPerson, setNewPerson] = useState<Person>({
    reasonForVisit: '',
    painLocation: '',
    dateOfOnsetInjury: '',
    ratePain: 0,
    typeOfPain: [],
    howOftenPain: '',
    relievePain: '',
    previousTreatments: '',
    painRelatedToInjury: '',
  })
  const [showWarning, setShowWarning] = useState(false)
  const [warningMessage, setWarningMessage] = useState('')

  const handleAddPerson = () => {
    if (!newPerson.reasonForVisit) {
      setWarningMessage('Please enter the reason for visit.')
      setShowWarning(true)
      return
    }
    if (!newPerson.painLocation) {
      setWarningMessage('Please enter the pain location.')
      setShowWarning(true)
      return
    }
    if (!newPerson.dateOfOnsetInjury) {
      setWarningMessage('Please enter the date of onset/injury.')
      setShowWarning(true)
      return
    }
    if (newPerson.ratePain === null || newPerson.ratePain === undefined) {
      setWarningMessage('Please rate the pain on a scale of 0-10.')
      setShowWarning(true)
      return
    }
    if (newPerson.typeOfPain.length === 0) {
      setWarningMessage('Please select at least one type of pain before saving.')
      setShowWarning(true)
      return
    }
    if (!newPerson.howOftenPain) {
      setWarningMessage('Please specify how often you experience this pain.')
      setShowWarning(true)
      return
    }
    if (!newPerson.relievePain) {
      setWarningMessage('Please specify what relieves the pain.')
      setShowWarning(true)
      return
    }
    if (!newPerson.previousTreatments) {
      setWarningMessage('Please specify previous treatments.')
      setShowWarning(true)
      return
    }
    if (!newPerson.painRelatedToInjury) {
      setWarningMessage('Please specify if the pain is related to an injury.')
      setShowWarning(true)
      return
    }

    setAssessmentRecords([...assessmentRecords, newPerson])
    setNewPerson({
      reasonForVisit: '',
      painLocation: '',
      dateOfOnsetInjury: '',
      ratePain: 0,
      typeOfPain: [],
      howOftenPain: '',
      relievePain: '',
      previousTreatments: '',
      painRelatedToInjury: '',
    })
    onClose()
  }

  const handleDeletePerson = (index: number) => {
    const updatedPersons = assessmentRecords.filter((_, i) => i !== index)
    setAssessmentRecords(updatedPersons)
  }

  const handleInputChange = (field: keyof Person, value: any) => {
    setNewPerson((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handlePainScaleChange = (value: number) => {
    setNewPerson(prev => ({
      ...prev,
      ratePain: value
    }));
  }

  const handlePainTypeChange = (value: string) => {
    setNewPerson(prev => ({
      ...prev,
      typeOfPain: prev.typeOfPain.includes(value)
        ? prev.typeOfPain.filter(type => type !== value)
        : [...prev.typeOfPain, value]
    }));
  }

  const handleCloseWarning = () => {
    setShowWarning(false);
  }
  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead style={{backgroundColor: '#2f3347'}}>
            <TableRow>
              <TableCell style={{color: 'white'}}>Actions</TableCell>
              <TableCell style={{color: 'white'}}>Reason for Visit</TableCell>
              <TableCell style={{color: 'white'}}>Pain Location</TableCell>
              <TableCell style={{color: 'white'}}>Date of Onset</TableCell>
              <TableCell style={{color: 'white'}}>Pain Scale</TableCell>
              <TableCell style={{color: 'white'}}>Type of Pain</TableCell>
              <TableCell style={{color: 'white'}}>How Often Pain</TableCell>
              <TableCell style={{color: 'white'}}>Relieve Pain</TableCell>
              <TableCell style={{color: 'white'}}>Previous Treatments</TableCell>
              <TableCell style={{color: 'white'}}>Pain Related to Injury</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assessmentRecords.length > 0 ? (
              assessmentRecords.map((person, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <IconButton onClick={() => handleDeletePerson(index)} disabled={disabled}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>{person.reasonForVisit}</TableCell>
                  <TableCell>{person.painLocation}</TableCell>
                  <TableCell>{dayjs(person.dateOfOnsetInjury).format("MM-DD-YYYY")}</TableCell>
                  <TableCell>{person.ratePain}</TableCell>
                  <TableCell>{person?.typeOfPain?.length > 0 ? person?.typeOfPain?.join(', ') : person?.typeOfPain}</TableCell>
                  <TableCell>{person.howOftenPain}</TableCell>
                  <TableCell>{person.relievePain}</TableCell>
                  <TableCell>{person.previousTreatments}</TableCell>
                  <TableCell>{person.painRelatedToInjury}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align='center' style={{fontWeight: 'bold', fontSize: '16px'}}>
                  No Records Added
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button variant='contained' onClick={onOpen} startIcon={<AddIcon />} sx={{mt: 2}} disabled={disabled}>
        Add Record
      </Button>

      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>Add New Assessment Record</DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: '1rem' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}
                    color='primary'
                  >
                    Reason for Visit/Complaint
                  </Typography>
                  <TextField
                    name='reasonForVisit'
                    label='Reason for Visit/Complaint'
                    placeholder='Reason for Visit/Complaint'
                    value={newPerson.reasonForVisit}
                    onChange={(e) => handleInputChange('reasonForVisit', e.target.value)}
                    size='small'
                    fullWidth
                    required
                    multiline
                    rows={3}
                    disabled={disabled}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}
                    color='primary'
                  >
                    Pain Location
                  </Typography>
                  <TextField
                    name='painLocation'
                    placeholder='Pain Location'
                    label='Pain Location'
                    value={newPerson.painLocation}
                    onChange={(e) => handleInputChange('painLocation', e.target.value)}
                    size='small'
                    fullWidth
                    required
                    multiline
                    rows={3}
                    disabled={disabled}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date of Onset/Injury"
                  value={newPerson.dateOfOnsetInjury ? dayjs(newPerson.dateOfOnsetInjury) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      handleInputChange('dateOfOnsetInjury', newValue.format('MM-DD-YYYY'));
                    }
                  }}
                  shouldDisableDate={(date: any) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date > today;
                  }}
                  disabled={disabled}
                  slotProps={{
                    field:{
                      readOnly: true,
                    },
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      required: true,
                      inputProps: { 
                        style: { cursor: 'pointer' },
                        readOnly: true,
                      },
                      onClick: (e) => {
                        // Prevent the click from bubbling up and trigger the date picker
                        e.preventDefault();
                        e.stopPropagation();
                        // Find and click the calendar icon to open the picker
                        const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                         e.currentTarget.querySelector('.MuiIconButton-root');
                        if (iconButton) {
                          (iconButton as HTMLElement).click();
                        }
                      }
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <Typography
                sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}
                color='primary'
              >
                Rate pain on a scale of 0-10
              </Typography>
              <FormControl component='fieldset' sx={{marginLeft: '1rem'}}>
                <RadioGroup
                  row
                  value={newPerson.ratePain}
                  onChange={(e) => handlePainScaleChange(Number(e.target.value))}
                >
                  {painScaleOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio disabled={disabled} />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
            <Typography
                sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}
                color='primary'
              >
                Indicate the type(s) of pain you are experiencing
              </Typography>
              <FormControl component='fieldset' sx={{ marginLeft: '1rem' }}>
                <FormGroup row>
                  {painTypeOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={
                        <Checkbox
                          checked={newPerson.typeOfPain.includes(option.value)}
                          onChange={() => handlePainTypeChange(option.value)}
                          disabled={disabled}
                        />
                      }
                      label={option.label}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name='howOftenPain'
                label='How often do you experience this pain?'
                value={newPerson.howOftenPain}
                onChange={(e) => handleInputChange('howOftenPain', e.target.value)}
                size='small'
                fullWidth
                required
                multiline
                rows={3}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name='relievePain'
                label='Does anything relieve the pain?'
                value={newPerson.relievePain}
                onChange={(e) => handleInputChange('relievePain', e.target.value)}
                size='small'
                fullWidth
                required
                multiline
                rows={3}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name='previousTreatments'
                label='Previous Treatments'
                value={newPerson.previousTreatments}
                onChange={(e) => handleInputChange('previousTreatments', e.target.value)}
                size='small'
                fullWidth
                required
                multiline
                rows={3}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name='painRelatedToInjury'
                label='Is your pain related to an injury? If so, please describe what happened.'
                value={newPerson.painRelatedToInjury}
                onChange={(e) => handleInputChange('painRelatedToInjury', e.target.value)}
                size='small'
                fullWidth
                required
                multiline
                rows={3}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={disabled}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleAddPerson}
            disabled={disabled || !newPerson.reasonForVisit || !newPerson.painLocation}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={showWarning} 
        autoHideDuration={6000} 
        onClose={handleCloseWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseWarning} severity="warning" sx={{ width: '100%' }}>
          {warningMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default PhysicalTherapistDialog
